// src/components/Blog.js
import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp
} from "firebase/firestore";
import { db }          from "../utils/firebase";
import { ADMIN_ADDRESS } from "../utils";

export default function Blog({ walletAddress }) {
  const [entries, setEntries] = useState([]);
  const [draft, setDraft] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});
  const bottomRef = useRef();

  useEffect(() => {
    const q = query(
      collection(db, "support-forum"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => {
        const docData = d.data();
        const createdAt = docData.createdAt?.toDate?.() || new Date();
        const replies = (docData.replies || []).map(r => ({
          ...r,
          createdAt: r.createdAt?.toDate?.() || new Date()
        }));
        return { id: d.id, ...docData, createdAt, replies };
      });
      setEntries(data);
    });
    return () => unsub();
  }, []);

  const canDelete = author =>
    walletAddress === author || walletAddress === ADMIN_ADDRESS;

  const postEntry = async e => {
    e.preventDefault();
    if (!draft.trim()) return;
    await addDoc(collection(db, "support-forum"), {
      author:    walletAddress,
      text:      draft.trim(),
      createdAt: serverTimestamp(),
      replies:   []
    });
    setDraft("");
  };

  const deletePost = async id => {
    const entry = entries.find(e => e.id === id)
    if (entry && canDelete(entry.author)) {
      await deleteDoc(doc(db, "support-forum", id))
    }
  }

  const postReply = async (e, postId) => {
    e.preventDefault();
    const text = (replyDrafts[postId] || "").trim();
    if (!text) return;
  
    const entryRef = doc(db, "support-forum", postId);
    const reply = {
      id:        Date.now().toString(),
      author:    walletAddress,
      text,
      createdAt: new Date()
    };
  
    await updateDoc(entryRef, {
      replies: [...entries.find(ent => ent.id === postId).replies, reply]
    });
  
    setReplyDrafts(prev => ({ ...prev, [postId]: "" }));
  };

  const deleteReply = async (postId, replyId) => {
    const entryRef = doc(db, "support-forum", postId)
    const orig = entries.find(e => e.id === postId)
    if (!orig) return
    const reply = orig.replies.find(r=>r.id===replyId)
    if (reply && canDelete(reply.author)) {
      const updated = orig.replies.filter(r => r.id !== replyId)
      await updateDoc(entryRef, { replies: updated })
    }
  }

  return (
    <div className="blog-container">
      <h2>🆘 Community Forum</h2>
      <div className="blog-entries">
        {entries.map(e => (
          <div key={e.id} className="blog-entry">
            {/* author & timestamp */}
            <div className="message-row">
              <span className="author">Wallet…{e.author.slice(-4)} </span>
              <span className="timestamp"> 
                {e.createdAt.toLocaleString()}
              </span>
              {canDelete(e.author) && (
                <button onClick={()=>deleteDoc(doc(db, "support-forum", e.id))} className="delete-btn">
                  🗑️
                </button>
              )}
            </div>
            {/* message */}
            <div className="message-text">{e.text}</div>

            {/* replies */}
            {e.replies.map(r => (
              <div key={r.id} className="reply-entry">
                <div className="message-row">
                  <span className="author">Wallet…{r.author.slice(-4)}</span>
                  <span className="timestamp">
                    {r.createdAt.toLocaleTimeString()}
                  </span>
                  {canDelete(r.author) && (
                    <button
                      onClick={()=>{
                        const entryRef = doc(db, "support-forum", e.id);
                        updateDoc(entryRef, {
                          replies: e.replies.filter(x => x.id !== r.id)
                        });
                      }}
                      className="delete-btn"
                    >🗑️</button>
                  )}
                </div>
                <div className="message-text">{r.text}</div>
              </div>
            ))}

            {/* reply form */}
            <form onSubmit={ev=>postReply(ev,e.id)} className="reply-form">
              <input
                type="text"
                value={replyDrafts[e.id]||""}
                onChange={ev=>setReplyDrafts(p=>({...p,[e.id]:ev.target.value}))}
                placeholder="Reply…"
                disabled={!walletAddress}
              />
              <button type="submit" disabled={!replyDrafts[e.id]?.trim()}>
                Reply
              </button>
            </form>
          </div>
        ))}
        <div ref={bottomRef}/>
      </div>

      {/* new post form */}
      <form onSubmit={postEntry} className="blog-input">
        <textarea
          value={draft}
          onChange={e=>setDraft(e.target.value)}
          placeholder="Ask anything just be polite…"
          rows={2}
          disabled={!walletAddress}
        />
        <button type="submit" disabled={!draft.trim()}>
          Post
        </button>
      </form>
    </div>
  );
}